import api from "@/api";

export default {
    state: {
        customerOffers: [],
        offerTypes: [],
        offerQuestions: {},
        offerToEdit: null,
    },
    getters: {
        customerOffers: (state) => state.customerOffers,
        offerTypes: (state) => state.offerTypes,
        offerQuestions: (state) => state.offerQuestions,
        offerToEdit: (state) => state.offerToEdit,
    },
    mutations: {
        setOffers: (state, data) => { state.customerOffers = data; },
        setOfferTypes: (state, data) => { state.offerTypes = data; },
        setOfferQuestions: (state, data) => { state.offerQuestions = data; },
        setOfferToEdit: (state, data) => { state.offerToEdit = data; }
    },
    actions: {
        showToast({ commit }, { type, message }) {
            commit("setToast", {
                display: true,
                type: type,
                message: message,
            }, { root: true });
        },

        getOfferTypes: async ({ commit, dispatch }) => {
            try {
                const response = await api({ requiresAuth: true }).get(`v1/policy-offers`);
                const { data } = response.data;
                commit("setOfferTypes", data);
                return data;
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Failed to load offer types."
                });
                throw error;
            }
        },

        getOfferQuestions: async ({ commit, dispatch }, offerId) => {
            try {
                const response = await api({ requiresAuth: true }).get(`v1/policy-offers/${offerId}/questions`);
                const { data } = response.data;
                commit("setOfferQuestions", data);
                return data;
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Failed to load offer questions."
                });
                throw error;
            }
        },


        getCustomerOffers: async ({ commit, dispatch }, customerId) => {
            try {
                const response = await api({ requiresAuth: true }).get(`v1/customer-policy-offers/customer/${customerId}`);
                const { data } = response.data;
                commit("setOffers", data);
                return data;
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Failed to load offers."
                });
                throw error;
            }
        },

        getSpecificOffer: async ({ commit, dispatch }, offerId) => {
            try {
                const response = await api({ requiresAuth: true }).get(`v1/customer-policy-offers/${offerId}`);
                const { data } = response.data;
                commit("setOfferToEdit", data);
                return data;
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Failed to load offer."
                });
                throw error;
            }
        },

        downloadOfferDocument: async ({ dispatch }, offerId) => {
            try {
                const response = await api({ requiresAuth: true }).get(`v1/customer-policy-offers/${offerId}/download-pdf`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `offer_${offerId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Failed to download offer document."
                });
                throw error;
            }
        },

        openOfferDocument: async ({ dispatch }, offerId) => {
            try {
                const response = await api({ requiresAuth: true }).get(`v1/customer-policy-offers/${offerId}/download-pdf`, { responseType: 'blob' });
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                window.open(url, '_blank');
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Failed to open offer document."
                });
                throw error;
            }
        },

        createCustomerOffer: async ({ dispatch }, offer) => {
            try {
                const response = await api({ requiresAuth: true }).post("v1/customer-policy-offers", offer);
                
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || "Offer created successfully!"
                });
                
                // Return response.data which contains { message, email_sent, id }
                return response.data;
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Could not create offer."
                });
                throw error;
            }
        },

        deleteCustomerOffer: async ({ dispatch }, { offerId, customerId }) => {
            try {
                const response = await api({ requiresAuth: true }).delete(`v1/customer-policy-offers/${offerId}`);
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || "Offer deleted."
                });

                await dispatch("getCustomerOffers", customerId);
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Failed to delete offer."
                });
                throw error;
            }
        },

        updateCustomerOffer: async ({ dispatch }, { offerId, offerData, customerId }) => {
            try {
                const response = await api({ requiresAuth: true }).patch(`v1/customer-policy-offers/${offerId}`, offerData);
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || "Offer updated successfully."
                });

                await dispatch("getCustomerOffers", customerId);
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Failed to update offer."
                });
                throw error;
            }
        },

        reSubmitCustomerOffer: async ({ dispatch }, offer) => {
            try {
                const response = await api({ requiresAuth: true }).post(`v1/customer-policy-offers/${offer.id}/send-email`);
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || "Offer resubmitted successfully."
                });

                await dispatch("getCustomerOffers", offer.customer_id);
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "Failed to resubmit offer."
                });
                throw error;
            }
        },

        addOfferFile: async ({ dispatch }, { offerId, files }) => {
            try {
                const formData = new FormData();
                files.forEach(file => formData.append("customer_policy_offer_files[]", file));
 
                const response = await api({ requiresAuth: true }).post(
                    `v1/customer-policy-offers/${offerId}/files`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || "Files uploaded successfully."
                });
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || "File upload failed."
                });
                throw error;
            }
        },
    },
}